import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js';
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { Card } from '../../ui';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { valorParaTexto } from '../../utils/Utils';
import { hexToRgba, valorCurto } from './uteis';
import styles from './FluxoCaixaCard.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type FluxoCaixaCardProps = {
  transacoes: Transacao[];
  inicio: Date;
  fim: Date;
};

/**
 * Fluxo de caixa do período: receitas vs despesas em barras, com agrupamento
 * adaptativo — por dia (até 31 dias), por semana (até 120) ou por mês.
 */
export default function FluxoCaixaCard({ transacoes, inicio, fim }: FluxoCaixaCardProps) {
  const { rotulos, receitas, despesas, agrupamento } = useMemo(() => {
    const dias = differenceInCalendarDays(fim, inicio) + 1;

    let inicios: Date[];
    let inicioDoGrupo: (data: Date) => Date;
    let rotuloDoGrupo: (data: Date) => string;
    let nomeAgrupamento: string;

    if (dias <= 31) {
      inicios = eachDayOfInterval({ start: inicio, end: fim });
      inicioDoGrupo = (data) => startOfDay(data);
      rotuloDoGrupo = (data) => format(data, 'd MMM', { locale: ptBR }).replace('.', '');
      nomeAgrupamento = 'dia';
    } else if (dias <= 120) {
      inicios = eachWeekOfInterval({ start: inicio, end: fim }, { weekStartsOn: 1 });
      inicioDoGrupo = (data) => startOfWeek(data, { weekStartsOn: 1 });
      rotuloDoGrupo = (data) => format(data, 'd MMM', { locale: ptBR }).replace('.', '');
      nomeAgrupamento = 'semana';
    } else {
      inicios = eachMonthOfInterval({ start: inicio, end: fim });
      inicioDoGrupo = (data) => startOfMonth(data);
      rotuloDoGrupo = (data) =>
        format(data, 'MMM yy', { locale: ptBR }).replace('.', '').replace(/^./, (letra) => letra.toUpperCase());
      nomeAgrupamento = 'mês';
    }

    const indicePorInicio: { [chave: number]: number } = {};
    inicios.forEach((data, indice) => {
      indicePorInicio[data.getTime()] = indice;
    });

    const somasReceitas = inicios.map(() => 0);
    const somasDespesas = inicios.map(() => 0);

    for (const transacao of transacoes) {
      const indice = indicePorInicio[inicioDoGrupo(new Date(transacao.data)).getTime()];
      if (indice === undefined) continue;
      if (transacao.tipo === 'R') somasReceitas[indice] += Number(transacao.valor);
      else somasDespesas[indice] += Number(transacao.valor);
    }

    return {
      rotulos: inicios.map(rotuloDoGrupo),
      receitas: somasReceitas,
      despesas: somasDespesas,
      agrupamento: nomeAgrupamento
    };
  }, [transacoes, inicio, fim]);

  // Cores lidas dos tokens no momento do render (acompanham o tema ativo).
  const estilosRaiz = getComputedStyle(document.documentElement);
  const corReceita = estilosRaiz.getPropertyValue('--success').trim();
  const corDespesa = estilosRaiz.getPropertyValue('--primary').trim();
  const corTexto = estilosRaiz.getPropertyValue('--textSecondary').trim();
  const corGrade = estilosRaiz.getPropertyValue('--border').trim();

  const dados: ChartData<'bar'> = {
    labels: rotulos,
    datasets: [
      {
        label: 'Receitas',
        data: receitas,
        backgroundColor: hexToRgba(corReceita, 0.4),
        hoverBackgroundColor: hexToRgba(corReceita, 0.75),
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 20
      },
      {
        label: 'Despesas',
        data: despesas,
        backgroundColor: hexToRgba(corDespesa, 0.85),
        hoverBackgroundColor: corDespesa,
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 20
      }
    ]
  };

  const opcoes: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item) => `${item.dataset.label}: ${valorParaTexto(item.parsed.y, true)}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: corTexto, font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }
      },
      y: {
        beginAtZero: true,
        grid: { color: hexToRgba(corGrade, 0.6), drawTicks: false },
        border: { display: false },
        ticks: {
          color: corTexto,
          font: { size: 11 },
          maxTicksLimit: 5,
          callback: (valor) => valorCurto(Number(valor))
        }
      }
    }
  };

  return (
    <Card className={styles.cartao}>
      <div className={styles.cabecalho}>
        <div className={styles.textos}>
          <h2 className={styles.titulo}>Fluxo de caixa</h2>
          <span className={styles.subtitulo}>Entradas e saídas por {agrupamento}</span>
        </div>
        <div className={styles.legenda}>
          <span className={styles.itemLegenda}>
            <span className={styles.pontoReceita} />
            Receitas
          </span>
          <span className={styles.itemLegenda}>
            <span className={styles.pontoDespesa} />
            Despesas
          </span>
        </div>
      </div>
      <div className={styles.areaGrafico}>
        <Bar data={dados} options={opcoes} />
      </div>
    </Card>
  );
}
